import React, { Suspense, useMemo, useRef, useState } from 'react';
import { prop } from 'ramda';

import { Scroller } from '@interlayer/js/app/components/common/Scroller';
import { FilterTabs } from '@interlayer/js/app/components/main/FilterTabs';
import { Button } from '@interlayer/js/components/common/Button';
import { Promotion } from './components/Promotion';

import { useTranslations } from '@core/js/hooks/i18n';
import { useUserSelector } from '@core/js/store/hooks';

import { calculationElementWidth } from '@interlayer/js/app/components/main/Promotions/helpers';
import { useHandlerViewAllActionClick } from '@interlayer/js/app/components/main/Promotions/hooks/useHandlerViewAllActionClick';
import { useInDesktop } from '@interlayer/js/app/hooks/device/hooks/useMediaQuery';
import useSegmentation from '@interlayer/js/app/hooks/useSegmentation';
import { clsx } from '@interlayer/js/app/utils/clsx.util';

import { ALL_TAG, NUMBER_OF_ELEMENTS_WITH_SMALL_GAP } from './constants';
import { TPromotionsProps, TPromotionType, VIEW_MODE } from './types';

import style from './Promotions.scss';

export const Promotions: React.FC<TPromotionsProps> = ({ config, isSuppressedDescription, portletInfo }) => {
    const t = useTranslations('playtech.mobile.system.promotions.');
    const isDesktop = useInDesktop();
    const { promotionsGroup = [], isReduceDescription, viewModeConfig } = config.promotionsConfig || {};
    const { viewMode, isEnableLink, numberOfElements } = viewModeConfig;
    const segmentation = useSegmentation();
    const [selectedTag, setSelectedTag] = useState(ALL_TAG);
    const [isInlineView, setViewMode] = useState(viewMode === VIEW_MODE.inline);
    const containerRef = useRef<HTMLDivElement>(null);
    const role = useUserSelector(prop('role'));
    const segmentedGroup = useMemo(() => {
        return promotionsGroup.filter((group) => {
            return group.isEnabled && segmentation(group.segmentation);
        });
    }, [promotionsGroup, segmentation]);
    const tags = useMemo(() => {
        return segmentedGroup.reduce((acc: string[], item) => {
            if (item.tag && !acc.includes(item.tag)) {
                item.tag.split(';').forEach((tag) => {
                    if (tag && !acc.includes(tag)) {
                        acc.push(tag);
                    }
                });
            }

            if (item.tagMultiple?.length) {
                item.tagMultiple.forEach((tag) => {
                    if (tag && !acc.includes(tag)) {
                        acc.push(tag);
                    }
                });
            }

            return acc;
        }, []);
    }, [segmentedGroup]);
    const filteredPromotions = useMemo(() => {
        const promosGroups = !tags.length || selectedTag === ALL_TAG
            ? segmentedGroup
            : segmentedGroup.filter((group) => {
                return group.tag?.includes(selectedTag) || group.tagMultiple?.includes(selectedTag);
            });

        return promosGroups.reduce((acc: TPromotionType[], group) => {
            group?.promotions?.forEach(promotion => {
                if (segmentation(promotion.userSeparation)) {
                    acc.push({ ...promotion, promoLink: group.promoLink });
                }
            });

            return acc;
        }, []);
    }, [segmentation, segmentedGroup, selectedTag, tags.length]);
    const isHiddenDescription = useMemo(() => {
        return isSuppressedDescription || filteredPromotions.every(({ description }) => !description);
    }, [filteredPromotions, isSuppressedDescription]);

    const handleClickBtn = useHandlerViewAllActionClick({
        setViewMode,
        config: viewModeConfig
    });

    const elementWidth = useMemo(() => {
        if (!isInlineView) {
            return 0;
        }

        return calculationElementWidth({ element: containerRef.current, numberOfElements: Number(numberOfElements), isDesktop });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isDesktop, isInlineView, numberOfElements, containerRef.current]);

    return (
        <div
            ref={containerRef}
            data-testid="Promotions_wrap"
            className={
                clsx(
                    style.wrap,
                    { [style.isInlineViewMode]: viewMode === VIEW_MODE.inline },
                    { [style.isSwitchedOnInlineViewMode]: isInlineView }
                )}
        >
            {isEnableLink && (
                <Button
                    data-testid="Promotions_viewAll"
                    view="link"
                    className={
                        clsx(
                            style.viewAll,
                            { [style.isPortletWithoutTitle]: !portletInfo.look.showTitle && role !== 'admin' }
                        )
                    }
                    onClick={handleClickBtn}
                >
                    {t(isInlineView ? 'viewAll' : 'back')}
                </Button>
            )}

            <Suspense fallback="">
                <FilterTabs
                    tags={tags.length > 0 ? [ALL_TAG, ...tags] : []}
                    selectedTag={selectedTag}
                    setSelectedTag={setSelectedTag}
                    classNames={{
                        ...isInlineView && { wrapper: style.filterTabs }
                    }}
                />
            </Suspense>

            {isInlineView
                ? (
                    <Scroller
                        classNames={{
                            container: clsx(
                                style.scrollContainer,
                                { [style.isSmallGap]: Number(numberOfElements) > NUMBER_OF_ELEMENTS_WITH_SMALL_GAP }
                            ),
                            wrapper: style.wrapper
                        }}
                    >
                        {filteredPromotions.map((item, index) => (
                            <Promotion
                                key={index}
                                index={index}
                                item={item}
                                isHiddenDescription={isHiddenDescription}
                                isReduceDescription={isReduceDescription}
                                classNames={{
                                    item: style.inlineMode
                                }}
                                customStyle={{
                                    ...Boolean(elementWidth) && { width: `${elementWidth}px` }
                                }}
                            />
                        ))}
                    </Scroller>
                ) : filteredPromotions.map((item, index) => (
                    <Promotion
                        key={index}
                        index={index}
                        item={item}
                        isHiddenDescription={isHiddenDescription}
                        isReduceDescription={isReduceDescription}
                    />
                ))
            }
        </div>
    );
};

export default Promotions;
